import {
    CheckboxRowGroup,
    ChipGroup,
    OptionCardGroup,
    RecommendedCard,
    StyleTileGrid,
} from '@rocco/components/questionnaire';
import type { GetMatchedStyleImage } from '~/modules/get-matched/api/get-matched-style-images.server';
import type { BuildHubOption } from '~/ui-rocco/hooks/use-build-hubs';

import type { QuestionnaireDefinition } from '../../questionnaire';
import type { MatchAnswers } from '../answers';
import type { GetMatchedOutcomeCopy } from '../GetMatchedQuestionnaire';
import {
    ADDITIONAL_PROFESSIONAL_OPTIONS,
    BUDGET_OPTIONS,
    PRIORITY_OPTIONS,
    PROJECT_TYPE_OPTIONS,
    RECOMMENDED_PROFESSIONAL,
    START_TIMING_OPTIONS,
    CONTACT_URGENCY_OPTIONS,
    USER_TYPE_OPTIONS,
    buildStyleTiles,
} from '../options';
import { BriefStep } from '../steps/BriefStep';
import { LocationStep } from '../steps/LocationStep';
import { ProjectStep } from '../steps/ProjectStep';
import { ProjectSummaryStep } from '../steps/ProjectSummaryStep';

/**
 * The suggestion the who-you-need step opens with. Held as an answer rather
 * than as card state so it travels with the brief when the reader leaves it
 * alone, which is the common case.
 */
export const PROFESSIONAL_INITIAL_ANSWERS: Partial<MatchAnswers> = {
    professionalTypes: [RECOMMENDED_PROFESSIONAL.title],
};

const toggle = (values: string[], value: string): string[] =>
    values.includes(value)
        ? values.filter((entry) => entry !== value)
        : [...values, value];

export interface ProfessionalDefinitionOptions {
    styleImages?: GetMatchedStyleImage[];
    paneImage?: string;
    /** Projects the reader already owns. Empty means the opening step never shows. */
    projects?: BuildHubOption[];
}

/**
 * Whether the reader is leaning on a project for the details it already holds.
 *
 * The three questions this gates are the ones a project can answer outright.
 * Everything else describes this request rather than the project, and is always
 * asked — including how soon they want to hear back, which is why the timeline
 * step is pre-answered but still shown.
 */
const answeredByProject = (answers: MatchAnswers): boolean =>
    answers.buildHubID !== null && answers.useProjectDetails;

/**
 * The professional brief: nine questions, in the order the design lays them out
 * on the canvas.
 *
 * That canvas order is the authority. The frame names skip the who-you-need
 * screen entirely and its own eyebrow reads "step 1 of 3" where its neighbours
 * read "of 8" — the labels are stale, the layout is not.
 */
export const buildProfessionalDefinition = ({
    styleImages,
    paneImage,
    projects = [],
}: ProfessionalDefinitionOptions = {}): QuestionnaireDefinition<MatchAnswers> => ({
    authPosition: 'first',
    id: 'get-matched-professional',
    paneImage,
    progressLabel: 'Get Matched',
    title: 'Get Matched',
    steps: [
        {
            heading: 'What is this brief for?',
            isApplicable: () => projects.length > 0,
            isComplete: (answers) => answers.projectChoice !== null,
            key: 'project',
            microLabel: 'your project',
            subheading:
                'If it is about a project you already have, we can carry over what we know.',
            render: ({ answers, next, update }) => (
                <ProjectStep
                    answers={answers}
                    next={next}
                    projects={projects}
                    update={update}
                />
            ),
        },
        {
            heading: "Here's what we'll use",
            isApplicable: (answers) => answers.buildHubID !== null,
            key: 'projectSummary',
            microLabel: 'from your project',
            subheading: (answers) => {
                const chosen = projects.find(
                    (entry) => Number(entry.ID) === answers.buildHubID,
                );

                // Named outright. "Your project" is vague to anyone who has
                // more than one, and this is the screen where they agree to
                // details they are not being shown the source of otherwise.
                return chosen
                    ? `Straight from ${chosen.Title}, so you do not have to say it twice.`
                    : 'Straight from your project, so you do not have to say it twice.';
            },
            render: ({ answers, update }) => (
                <ProjectSummaryStep
                    answers={answers}
                    projects={projects}
                    update={update}
                />
            ),
        },
        {
            // "First" is only true when nothing was asked before it. A reader
            // with a project has already answered which one.
            heading:
                projects.length > 0
                    ? 'Tell us who you are'
                    : 'First, tell us who you are',
            isComplete: (answers) => Boolean(answers.userType),
            key: 'userType',
            microLabel: 'who you are',
            subheading:
                'This helps our team match you with professionals who work on projects like yours.',
            render: ({ answers, next, update }) => (
                <OptionCardGroup
                    label="Who you are"
                    onChange={(value) => {
                        update({ userType: value });
                        next();
                    }}
                    options={USER_TYPE_OPTIONS}
                    value={answers.userType}
                />
            ),
        },
        {
            heading: 'Where is your project located?',
            isApplicable: (answers) => !answeredByProject(answers),
            isComplete: (answers) => answers.region.trim().length > 0,
            key: 'region',
            microLabel: 'project location',
            subheading:
                'So we can shortlist professionals who work in your area and know it well.',
            render: ({ answers, update }) => (
                <LocationStep answers={answers} update={update} />
            ),
        },
        {
            heading: 'Which best describes your project?',
            isApplicable: (answers) => !answeredByProject(answers),
            isComplete: (answers) => Boolean(answers.projectType),
            key: 'projectType',
            microLabel: 'project type',
            subheading:
                'So we can match you with professionals who specialise in work like yours.',
            render: ({ answers, next, update }) => (
                <OptionCardGroup
                    label="Project type"
                    onChange={(value) => {
                        update({ projectType: value });
                        next();
                    }}
                    options={PROJECT_TYPE_OPTIONS}
                    value={answers.projectType}
                />
            ),
        },
        {
            heading: 'Who do you want to work with?',
            isComplete: (answers) => answers.professionalTypes.length > 0,
            key: 'professionalTypes',
            microLabel: 'recommended',
            subheading:
                "We've suggested a starting point for your project — adjust as you like.",
            render: ({ answers, update }) => (
                <div className="flex flex-col gap-6">
                    <RecommendedCard
                        checked={answers.professionalTypes.includes(
                            RECOMMENDED_PROFESSIONAL.title,
                        )}
                        description={RECOMMENDED_PROFESSIONAL.description}
                        onCheckedChange={() =>
                            update({
                                professionalTypes: toggle(
                                    answers.professionalTypes,
                                    RECOMMENDED_PROFESSIONAL.title,
                                ),
                            })
                        }
                        title={RECOMMENDED_PROFESSIONAL.title}
                    />

                    <div className="flex flex-col gap-2">
                        <p className="font-sans text-4 leading-[1.3] font-medium text-[#161616]">
                            Add others to your team (optional)
                        </p>
                        <p className="font-sans text-4 leading-[1.3] text-[#878787]">
                            A build usually starts with an architect. If
                            that&rsquo;s not your project, pick what type of
                            professional you need.
                        </p>
                    </div>

                    <CheckboxRowGroup
                        label="Add others to your team"
                        onChange={(values) =>
                            update({
                                professionalTypes: [
                                    ...(answers.professionalTypes.includes(
                                        RECOMMENDED_PROFESSIONAL.title,
                                    )
                                        ? [RECOMMENDED_PROFESSIONAL.title]
                                        : []),
                                    ...values,
                                ],
                            })
                        }
                        options={ADDITIONAL_PROFESSIONAL_OPTIONS}
                        values={answers.professionalTypes.filter(
                            (entry) => entry !== RECOMMENDED_PROFESSIONAL.title,
                        )}
                    />
                </div>
            ),
        },
        {
            columnWidth: 'wide',
            heading: 'What draws you in?',
            isComplete: (answers) => answers.styles.length > 0,
            key: 'style',
            microLabel: 'your style',
            subheading:
                "Pick the styles that resonate. It helps us match you with practices whose work you'll love.",
            render: ({ answers, update }) => (
                <StyleTileGrid
                    label="Your style"
                    onChange={(values) => update({ styles: values })}
                    options={buildStyleTiles(styleImages)}
                    values={answers.styles}
                />
            ),
        },
        {
            heading: 'What matters most to you?',
            isComplete: (answers) => answers.priorities.length > 0,
            key: 'priorities',
            microLabel: 'primary requirements',
            subheading:
                'This helps our team match you with the right fit. Pick as many as you like.',
            render: ({ answers, update }) => (
                <CheckboxRowGroup
                    label="What matters most"
                    onChange={(values) => update({ priorities: values })}
                    options={PRIORITY_OPTIONS}
                    values={answers.priorities}
                />
            ),
        },
        {
            heading: "What's your project budget?",
            isApplicable: (answers) => !answeredByProject(answers),
            isComplete: (answers) => Boolean(answers.budget),
            key: 'budget',
            microLabel: 'project budget',
            subheading:
                'Approximate figures help us find practices suited to your project scope.',
            render: ({ answers, next, update }) => (
                <OptionCardGroup
                    label="Project budget"
                    onChange={(value) => {
                        update({ budget: value });
                        next();
                    }}
                    options={BUDGET_OPTIONS}
                    value={answers.budget}
                />
            ),
        },
        {
            columnWidth: 'wide',
            heading: 'What’s your timeline?',
            isComplete: (answers) =>
                Boolean(answers.startTiming) && Boolean(answers.contactUrgency),
            key: 'timeline',
            subheading:
                'So we match you with professionals who are available when you need them.',
            render: ({ answers, update }) => (
                <div className="flex flex-col gap-8">
                    <ChipGroup
                        label="When would you like to start?"
                        onChange={(value) => update({ startTiming: value })}
                        options={START_TIMING_OPTIONS}
                        value={answers.startTiming}
                    />
                    <ChipGroup
                        label="How soon would you like to hear from professionals?"
                        onChange={(value) => update({ contactUrgency: value })}
                        options={CONTACT_URGENCY_OPTIONS}
                        value={answers.contactUrgency}
                    />
                </div>
            ),
        },
        {
            columnWidth: 'wide',
            heading: 'Tell us a bit more about your project',
            isComplete: (answers) => answers.description.trim().length > 0,
            key: 'brief',
            microLabel: 'project description',
            subheading:
                'The more we know, the better the match. Include site, scope, timing and anything else that matters.',
            render: ({ answers, update }) => (
                <BriefStep
                    answers={answers}
                    placeholder="e.g. We're planning a 4-bedroom new build on a hillside site in Herne Bay. We want strong connection to the harbour, open-plan living, and a calm, minimal aesthetic. We have planning consent in progress."
                    update={update}
                />
            ),
        },
    ],
});

export const buildProfessionalOutcome = (
    firstName?: string,
): GetMatchedOutcomeCopy => ({
    actionLabel: 'Back to ArchiPro',
    body: `${firstName ? `Thank you, ${firstName}. ` : ''}Our team will review your brief and personally match you with 2–3 professionals whose work, availability and approach align with your project.`,
    footnote:
        'Every match is quality-checked by our team — so you only hear from people worth your time. A confirmation has been sent to your email.',
    heading: 'Brief received',
    steps: [
        {
            description:
                'Usually within a day — a real person, not an algorithm.',
            title: 'We review your brief',
        },
        {
            description:
                'Architects whose work, availability and approach fit your project.',
            title: 'We hand-pick your matches',
        },
        {
            description:
                'Directly, so you can start the conversation on your terms.',
            title: 'We introduce you',
        },
    ],
});
